/**
 * SettingsDialog Component
 *
 * Modal dialog for managing application settings, including YouTube OAuth credentials.
 * Fetches credential status on open, displays masked values for configured credentials,
 * and allows saving new credential values via CredentialsService.
 *
 * Requirements: 2.1–2.5, 3.1–3.5, 4.1–4.6
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Skeleton,
    TextField,
    Typography,
} from '@mui/material';
import { useNotification } from '../contexts/NotificationContext';
import { CredentialsService } from '../services/credentials-service';
import type { CredentialStatus } from '../types';

export interface SettingsDialogProps {
    open: boolean;
    onClose: () => void;
}

export function SettingsDialog({ open, onClose }: SettingsDialogProps) {
    const { notify } = useNotification();

    const [credentialStatus, setCredentialStatus] = useState<CredentialStatus | null>(null);
    const [clientId, setClientId] = useState('');
    const [clientSecret, setClientSecret] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dirty, setDirty] = useState(false);

    const clientIdRef = useRef<HTMLInputElement>(null);

    const fetchCredentialStatus = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const service = new CredentialsService();
            const status = await service.getCredentialStatus();
            setCredentialStatus(status);
            // Initialize fields with masked values or empty
            setClientId(status.client_id.configured ? (status.client_id.masked_value ?? '') : '');
            setClientSecret(status.client_secret.configured ? (status.client_secret.masked_value ?? '') : '');
            setDirty(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load credential status');
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch credential status when dialog opens
    useEffect(() => {
        if (open) {
            fetchCredentialStatus();
        }
    }, [open, fetchCredentialStatus]);

    // Focus first input after loading completes
    useEffect(() => {
        if (open && !loading && clientIdRef.current) {
            clientIdRef.current.focus();
        }
    }, [open, loading]);

    const handleClientIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setClientId(e.target.value);
        setDirty(true);
    };

    const handleClientSecretChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setClientSecret(e.target.value);
        setDirty(true);
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const service = new CredentialsService();

            // Only send fields that differ from the masked display
            const sendClientId = clientId !== (credentialStatus?.client_id.masked_value ?? '')
                ? clientId : undefined;
            const sendClientSecret = clientSecret !== (credentialStatus?.client_secret.masked_value ?? '')
                ? clientSecret : undefined;

            const updatedStatus = await service.saveCredentials(sendClientId, sendClientSecret);
            setCredentialStatus(updatedStatus);
            setClientId(updatedStatus.client_id.configured ? (updatedStatus.client_id.masked_value ?? '') : '');
            setClientSecret(updatedStatus.client_secret.configured ? (updatedStatus.client_secret.masked_value ?? '') : '');
            setDirty(false);
            notify({ message: 'Credentials saved successfully', severity: 'success', autoCloseDuration: 4000 });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save credentials');
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        // Reset state on close without saving
        setError(null);
        setDirty(false);
        onClose();
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                aria-labelledby="settings-dialog-title"
                data-testid="settings-dialog"
            >
                <DialogTitle id="settings-dialog-title">Settings</DialogTitle>
                <DialogContent>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1, mb: 2 }}>
                        YouTube Credentials
                    </Typography>

                    {loading ? (
                        <Box data-testid="settings-loading">
                            <Skeleton variant="rectangular" height={56} sx={{ mb: 2, borderRadius: 1 }} />
                            <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
                        </Box>
                    ) : (
                        <Box>
                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }} data-testid="settings-error">
                                    {error}
                                </Alert>
                            )}
                            <TextField
                                fullWidth
                                margin="dense"
                                label="Google OAuth Client ID"
                                type="password"
                                value={clientId}
                                onChange={handleClientIdChange}
                                placeholder="Enter OAuth Client ID"
                                inputRef={clientIdRef}
                                inputProps={{
                                    'aria-label': 'Google OAuth Client ID',
                                    'data-testid': 'settings-client-id-input',
                                }}
                                data-testid="settings-client-id"
                            />
                            <TextField
                                fullWidth
                                margin="dense"
                                label="Google OAuth Client Secret"
                                type="password"
                                value={clientSecret}
                                onChange={handleClientSecretChange}
                                placeholder="Enter OAuth Client Secret"
                                inputProps={{
                                    'aria-label': 'Google OAuth Client Secret',
                                    'data-testid': 'settings-client-secret-input',
                                }}
                                data-testid="settings-client-secret"
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleClose}
                        data-testid="settings-cancel-button"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={!dirty || saving}
                        data-testid="settings-save-button"
                        startIcon={saving ? <CircularProgress size={16} /> : undefined}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
